import { useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';

import {
  assignMemberRole,
  getMemberRoles,
  listRoles,
  removeMemberRole,
  validateApiResponse,
} from '../../../services';
import { MEMBER_STATUS } from '../constants/copy';
import { useMemberTabData } from './useMemberTabData';
import { useMemberTabMutation } from './useMemberTabMutation';
import { useToastRef } from './useToastRef';

const mapRole = (raw) => ({
  id: raw.id,
  name: raw.name,
  description: raw.description ?? '',
  isSystem: raw.type === 'system',
  permissionCount: raw.permission_count ?? 0,
  assignedBy: raw.assigned_by ?? '',
  assignedAt: raw.assigned_at ?? null,
});

/**
 * Roles of a member and the roles still to assign: `{ roles, assignable, isLoading, error }` plus
 * `assign(roleId)` and `remove(roleId)`. Both resolve `true` when it worked (and refetch) and toast
 * an error otherwise: the prototype confirms neither with a toast.
 *
 * @param {{ id: string }} member UI member
 */
export const useMemberRoles = (member) => {
  const { t } = useTranslation('people-access');
  const toastRef = useToastRef();
  const memberId = member.id;

  const load = useCallback(async () => {
    const [assigned, catalog] = await Promise.all([getMemberRoles(memberId), listRoles()]);
    const roles = validateApiResponse(assigned, t('memberTabs.loadError')).body.items.map(mapRole);
    const all = validateApiResponse(catalog, t('memberTabs.loadError')).body.items;
    const taken = new Set(roles.map((r) => r.id));
    return {
      roles,
      assignable: all
        .filter((r) => !taken.has(r.id))
        .map((r) => ({ id: r.id, name: r.name, description: r.description ?? '', isSystem: r.type === 'system' })),
    };
  }, [memberId, t]);
  const { data, isLoading, error, refresh, retry } = useMemberTabData(memberId, load, {
    enabled: member.status !== MEMBER_STATUS.REMOVED,
  });

  const { mutate } = useMemberTabMutation({
    refresh,
    failedTitleKey: 'memberTabs.roles.toast.failedTitle',
    toastRef,
  });

  const assign = useCallback((roleId) => mutate(() => assignMemberRole(memberId, roleId)), [mutate, memberId]);
  const remove = useCallback((roleId) => mutate(() => removeMemberRole(memberId, roleId)), [mutate, memberId]);

  return useMemo(
    () => ({
      roles: data?.roles ?? [],
      assignable: data?.assignable ?? [],
      isLoading,
      error,
      assign,
      remove,
      reload: retry,
    }),
    [data, isLoading, error, assign, remove, retry],
  );
};
